import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { AlertTriangle, FileUp, Keyboard } from 'lucide-react';
import React, {
	type ChangeEvent,
	type ClipboardEvent,
	type Dispatch,
	type FormEvent,
	type SetStateAction,
	useState
} from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import employeeImportConfig from '@/../config/employee-import.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Modal from '@/components/shared/Modal';
import Spinner from '@/components/shared/Spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useExtractionUpload } from '@/features/Quote/hooks/useExtractionUpload';
import { cn } from '@/lib/utils';
import apiService from '@/services/apiService';
import { handleNumericPaste, prepareExtractedEmployees } from '@/services/utils';
import { getValidationMessage, validateWageSum } from '@/services/validators';
import type { Employee, ExtractionResult } from '@/types/employee';

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

interface FormData {
	wage_sum: string;
}

interface Errors {
	wage_sum?: string;
}

interface SubmitStatus {
	type: string;
	message: string;
}

type InputMode = 'estimate' | 'upload';

interface ModalWageInfoProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onAfterSubmit?: () => void;
}

function ModalWageInfo({
	open,
	onOpenChange,
	onAfterSubmit
}: ModalWageInfoProps): React.JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const context = useAppContext();
	const { premiumVarManagement } = context || {};

	// Input mode state
	const [inputMode, setInputMode] = useState<InputMode>('estimate');

	// Wage sum form state
	const [formData, setFormData] = useState<FormData>({
		wage_sum: ''
	});
	const [errors, setErrors] = useState<Errors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({ type: '', message: '' });

	// File upload state — the FilePond/captcha/upload/polling lifecycle lives in the
	// shared hook; only the success message is specific to this modal.
	const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

	// ===== WAGE SUM FORM HANDLERS =====
	const validateForm = (): boolean => {
		const errorKey = validateWageSum(formData.wage_sum, 8);

		if (errorKey) {
			const errorMessage = getValidationMessage(errorKey);
			setErrors({ wage_sum: errorMessage });
			return false;
		}

		setErrors({});
		return true;
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;

		// Only allow numeric input for wage sum field, no spaces, and max 8 characters
		if (id === 'wage_sum' && (!/^\d*$/.test(value) || value.length > 8)) {
			return;
		}

		setFormData((prevState) => ({
			...prevState,
			[id]: value
		}));
		// Clear error when user starts typing
		if (errors[id as keyof Errors]) {
			setErrors((prev) => ({ ...prev, [id]: '' }));
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		handleNumericPaste(
			e,
			'wage_sum',
			8,
			setFormData as unknown as Dispatch<SetStateAction<Record<string, unknown>>>,
			() => {
				if (errors.wage_sum) {
					setErrors((prev) => ({ ...prev, wage_sum: '' }));
				}
			}
		);
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!id) {
			setErrors({ wage_sum: t('modals.wageInfo.noIdError') });
			return;
		}

		setIsSubmitting(true);
		setSubmitStatus({ type: '', message: '' });

		try {
			const payload = {
				wage_sum: Number.parseInt(formData.wage_sum.trim(), 10)
			};

			await apiService.wageInfo.update(id, payload);
			onAfterSubmit?.();
			onOpenChange(false);
		} catch (error) {
			setSubmitStatus({
				type: 'error',
				message: t('common.error')
			});
			console.warn(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// ===== FILE UPLOAD HANDLERS =====
	const handleImport = async (
		employeeData: Employee[],
		fail: (message: string | null) => void,
		stop: () => void,
		reset: () => void
	) => {
		if (!premiumVarManagement) {
			fail(t('common.error'));
			stop();
			reset();
			return;
		}

		try {
			// Clean up the extracted data to ensure API compatibility
			const cleanedEmployees = prepareExtractedEmployees(employeeData);

			// Replace existing employees using the premiumVarManagement system
			await premiumVarManagement.employees.replace(cleanedEmployees);

			// Persist the imported employee data to the backend
			if (id) {
				// Update wage info to trigger status transition
				await apiService.wageInfo.update(id, {
					wage_sum: Math.round(cleanedEmployees.reduce((sum, emp) => sum + (emp.wage || 0), 0))
				});

				await premiumVarManagement.employees.save(id, cleanedEmployees);
			}

			setUploadSuccess(
				t('modals.wageInfo.upload.successMessage', {
					count: cleanedEmployees.length
				})
			);

			// Close modal after brief delay to show success
			onAfterSubmit?.();
			setTimeout(() => {
				onOpenChange(false);
			}, 2000);
		} catch (err) {
			console.error('Import error:', err);
			fail(err instanceof Error ? err.message : t('common.error'));
			// Drop the file too, so the dropzone comes back and the user can retry.
			reset();
		} finally {
			stop();
		}
	};

	const {
		files,
		setFiles,
		isProcessing,
		error: uploadError,
		setError: setUploadError,
		filePondRef,
		handleProcessFile,
		clearPolling,
		clearFiles,
		stopProcessing
	} = useExtractionUpload<ExtractionResult>({
		upload: apiService.import.upload,
		fetchStatus: apiService.import.fetch,
		onCompleted: async (result, { setError: fail, clearFiles: reset, stopProcessing: stop }) => {
			if (result?.employee_list && result.employee_list.length > 0) {
				await handleImport(result.employee_list, fail, stop, reset);
			} else {
				fail(t('errors.noEmployeesFound'));
				stop();
				reset();
			}
		}
	});

	// Reset state when mode changes. Tearing down the poller isn't enough: an
	// upload abandoned mid-flight would otherwise leave `isProcessing` stuck on,
	// pinning the "analyzing" alert and hiding the dropzone with no poller left
	// to clear it.
	const handleModeChange = (mode: InputMode) => {
		setInputMode(mode);
		setSubmitStatus({ type: '', message: '' });
		setErrors({});
		setUploadError(null);
		setUploadSuccess(null);
		clearPolling();
		stopProcessing();
		clearFiles();
	};

	return (
		<Modal
			id="modalWageInfo"
			open={open}
			onOpenChange={onOpenChange}
			size="lg"
			backdrop="static"
			keyboard={false}
		>
			<div className="py-6 px-4">
				<div className="text-2xl font-bold mb-3">{t('modals.wageInfo.title')}</div>
				<p className="mb-8 text-muted-foreground leading-relaxed">
					<span className="sm:inline-block">
						<Trans
							i18nKey="modals.wageInfo.introductionChoice"
							components={[
								<span key="0" className="font-semibold" />,
								<span key="1" className="font-semibold" />
							]}
						/>
					</span>
				</p>

				{/* Mode Toggle */}
				<Tabs
					value={inputMode}
					onValueChange={(value) => handleModeChange(value as InputMode)}
					className="mb-8"
				>
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger
							value="estimate"
							data-testid="wage-info-mode-estimate"
							className="flex items-center gap-2"
						>
							<Keyboard className="h-4 w-4" />
							<span className="sm:hidden">{t('modals.wageInfo.modeEstimateShort')}</span>
							<span className="hidden sm:inline">{t('modals.wageInfo.modeEstimate')}</span>
						</TabsTrigger>
						<TabsTrigger
							value="upload"
							data-testid="wage-info-mode-upload"
							className="flex items-center gap-2"
						>
							<FileUp className="h-4 w-4" />
							<span className="sm:hidden">{t('modals.wageInfo.modeUploadShort')}</span>
							<span className="hidden sm:inline">{t('modals.wageInfo.modeUpload')}</span>
						</TabsTrigger>
					</TabsList>
				</Tabs>

				{/* Estimate Mode */}
				{inputMode === 'estimate' && (
					<>
						{submitStatus.message && (
							<Alert
								variant={submitStatus.type === 'success' ? 'success' : 'destructive'}
								className="mb-6"
							>
								<AlertDescription>{submitStatus.message}</AlertDescription>
							</Alert>
						)}

						<form onSubmit={handleSubmit} noValidate className="space-y-4 text-left">
							<Field data-invalid={!!errors.wage_sum}>
								<FieldLabel htmlFor="wage_sum">{t('modals.wageInfo.inputLabel')}</FieldLabel>
								<div className="relative">
									<span className="absolute left-3 top-5 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
										€
									</span>
									<Input
										type="text"
										id="wage_sum"
										value={formData.wage_sum}
										onChange={handleChange}
										onPaste={handlePaste}
										disabled={isSubmitting}
										placeholder="500000"
										data-testid="modal-wage-sum-input"
										aria-invalid={!!errors.wage_sum}
										className={cn('pl-8', errors.wage_sum && 'border-destructive')}
									/>
									{errors.wage_sum && (
										<FieldError data-testid="input-feedback-wage-sum">{errors.wage_sum}</FieldError>
									)}
								</div>
							</Field>

							<p className="text-sm text-muted-foreground">{t('modals.wageInfo.estimateHint')}</p>

							<Button
								type="submit"
								className="w-full mt-6"
								size="lg"
								loading={isSubmitting}
								disabled={isSubmitting}
								data-testid="modal-wage-info-submit-button"
							>
								{t('modals.wageInfo.submitButton')}
							</Button>
						</form>
					</>
				)}

				{/* Upload Mode */}
				{inputMode === 'upload' && (
					<div data-testid="wage-info-upload-section">
						{uploadError && (
							<Alert variant="destructive" className="mb-4">
								<AlertTriangle className="h-4 w-4" />
								<AlertDescription className="max-w-sm leading-relaxed">
									{uploadError}
								</AlertDescription>
							</Alert>
						)}

						{uploadSuccess && (
							<Alert variant="success" className="mb-4">
								<div className="flex items-center">
									<Spinner size="sm" variant="current" className="mr-3" visible={true} />
									<div>
										<div className="font-medium">{uploadSuccess}</div>
										<small className="text-muted-foreground">
											{t('modals.wageInfo.upload.processingMessage')}
										</small>
									</div>
								</div>
							</Alert>
						)}

						{!uploadSuccess && !isProcessing && (
							<>
								<p className="text-base text-muted-foreground mb-5 leading-relaxed">
									{t('modals.wageInfo.upload.description')}
								</p>

								<FilePond
									ref={filePondRef}
									{...employeeImportConfig}
									credits={false}
									files={files}
									onupdatefiles={setFiles}
									// Deliberately leaves the error alone: FilePond fires this for
									// programmatic removal too, so clearing here would wipe the
									// message the failure paths set just before calling clearFiles.
									// onaddfile clears it when the user tries again.
									onremovefile={() => {
										clearPolling();
										stopProcessing();
									}}
									onaddfile={(err) => {
										if (!err) {
											setUploadError(null);
											setUploadSuccess(null);
										}
									}}
									server={
										{
											process: handleProcessFile,
											revert: null
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
										} as any
									}
									data-testid="wage-info-file-upload"
								/>

								<p className="text-xs text-muted-foreground mt-2">
									{t('modals.wageInfo.upload.formats')}
								</p>
							</>
						)}

						{isProcessing && !uploadSuccess && (
							<Alert variant="info" className="mt-4">
								<div className="flex items-center">
									<Spinner size="sm" variant="current" className="mr-3" visible={true} />
									<div>
										<div className="font-medium">{t('common.fileProcessing')}</div>
										<small className="text-muted-foreground">
											{t('modals.wageInfo.upload.analyzingMessage')}
										</small>
									</div>
								</div>
							</Alert>
						)}
					</div>
				)}
			</div>
		</Modal>
	);
}

export default ModalWageInfo;
